/**
 * AnimatedModal — Vengeance UI inspired modal with Framer Motion
 * Uses backdropVariants (fade + blur) + modalVariants (scale + spring)
 *
 * Usage:
 *   import AnimatedModal from './AnimatedModal';
 *
 *   <AnimatedModal
 *     isOpen={showModal}
 *     onClose={() => setShowModal(false)}
 *     title="Create Match"
 *     footer={<><button onClick={...}>Cancel</button><button>Create</button></>}
 *   >
 *     {children}
 *   </AnimatedModal>
 *
 *   // Or for inline footer (no wrapper needed):
 *   <AnimatedModal isOpen={showModal} onClose={() => setShowModal(false)} title="...">
 *     <form onSubmit={handleSubmit} ...>
 *       ...
 *     </form>
 *   </AnimatedModal>
 */

import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { backdropVariants, modalVariants } from '../animations';

/**
 * Backdrop with blur fade-in
 */
function Backdrop({ onClick }) {
  return (
    <motion.div
      variants={backdropVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      onClick={onClick}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.72)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '1rem',
        overflowY: 'auto',
        cursor: 'pointer',
      }}
    >
      {/* Stop click propagation so clicking content doesn't close */}
      <div
        onClick={e => e.stopPropagation()}
        style={{ display: 'contents' }}
      />
    </motion.div>
  );
}

/**
 * Modal content panel with scale + spring entrance
 */
function ModalPanel({ children, className = '', style = {}, onClose, title, footer, maxWidth = '720px' }) {
  return (
    <motion.div
      variants={modalVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onClick={e => e.stopPropagation()}
      style={{
        background: 'var(--bg-elevated, #1a1a24)',
        border: '1px solid var(--border-default, rgba(255,255,255,0.09))',
        borderRadius: '16px',
        width: '100%',
        maxWidth,
        maxHeight: '85vh',
        overflowY: 'auto',
        boxShadow: '0 8px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)',
        position: 'relative',
        cursor: 'default',
        ...style,
      }}
      className={className}
    >
      {/* Header */}
      {title && (
        <div style={{
          padding: '1.25rem 1.5rem',
          borderBottom: '1px solid var(--border-subtle, rgba(255,255,255,0.06))',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}>
          <h2 style={{
            margin: 0,
            fontFamily: 'var(--font-display, var(--font-sans))',
            fontWeight: 700,
            fontSize: '1rem',
            color: 'var(--text-primary, #e8e8ed)',
            letterSpacing: '-0.01em',
          }}>
            {title}
          </h2>
          <button
            onClick={onClose}
            title="Close"
            style={{
              background: 'transparent',
              border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))',
              borderRadius: '8px',
              color: 'var(--text-secondary, #8b8b9e)',
              cursor: 'pointer',
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s ease',
              flexShrink: 0,
              padding: 0,
            }}
            onMouseOver={e => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
              e.currentTarget.style.color = 'var(--text-primary, #e8e8ed)';
              e.currentTarget.style.borderColor = 'var(--border-hover, rgba(255,255,255,0.14))';
            }}
            onMouseOut={e => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--text-secondary, #8b8b9e)';
              e.currentTarget.style.borderColor = 'var(--border-subtle, rgba(255,255,255,0.06))';
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}

      {/* Body */}
      <div style={{
        padding: title ? '1.25rem 1.5rem' : '1.5rem',
      }}>
        {children}
      </div>

      {/* Footer */}
      {footer && (
        <div style={{
          padding: '1rem 1.5rem',
          borderTop: '1px solid var(--border-subtle, rgba(255,255,255,0.06))',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '0.6rem',
          flexShrink: 0,
        }}>
          {footer}
        </div>
      )}
    </motion.div>
  );
}

/**
 * Main AnimatedModal export
 */
export default function AnimatedModal({
  isOpen,
  onClose,
  title,
  footer,
  children,
  className,
  style,
  maxWidth,
}) {
  // Lock body scroll while open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // ESC to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.72)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem',
            overflowY: 'auto',
            cursor: 'pointer',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ display: 'contents' }}
          >
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={e => e.stopPropagation()}
              style={{
                background: 'var(--bg-elevated, #1a1a24)',
                border: '1px solid var(--border-default, rgba(255,255,255,0.09))',
                borderRadius: '16px',
                width: '100%',
                maxWidth: maxWidth || '720px',
                maxHeight: '85vh',
                overflowY: 'auto',
                boxShadow: '0 8px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)',
                cursor: 'default',
                ...style,
              }}
              className={className}
            >
              {/* Header */}
              {title && (
                <div style={{
                  padding: '1.25rem 1.5rem',
                  borderBottom: '1px solid var(--border-subtle, rgba(255,255,255,0.06))',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexShrink: 0,
                }}>
                  <h2 style={{
                    margin: 0,
                    fontFamily: 'var(--font-display, var(--font-sans))',
                    fontWeight: 700,
                    fontSize: '1rem',
                    color: 'var(--text-primary, #e8e8ed)',
                    letterSpacing: '-0.01em',
                  }}>
                    {title}
                  </h2>
                  <button
                    onClick={onClose}
                    title="Close"
                    aria-label="Close modal"
                    style={{
                      background: 'transparent',
                      border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))',
                      borderRadius: '8px',
                      color: 'var(--text-secondary, #8b8b9e)',
                      cursor: 'pointer',
                      width: 32,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.15s ease',
                      flexShrink: 0,
                      padding: 0,
                    }}
                    onMouseOver={e => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                      e.currentTarget.style.color = 'var(--text-primary, #e8e8ed)';
                      e.currentTarget.style.borderColor = 'var(--border-hover, rgba(255,255,255,0.14))';
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'var(--text-secondary, #8b8b9e)';
                      e.currentTarget.style.borderColor = 'var(--border-subtle, rgba(255,255,255,0.06))';
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
              )}

              {/* Body */}
              <div style={{
                padding: title ? '1.25rem 1.5rem' : '1.5rem',
              }}>
                {children}
              </div>

              {/* Footer */}
              {footer && (
                <div style={{
                  padding: '1rem 1.5rem',
                  borderTop: '1px solid var(--border-subtle, rgba(255,255,255,0.06))',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '0.6rem',
                  flexShrink: 0,
                }}>
                  {footer}
                </div>
              )}
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
